import { inject, Injectable, OnDestroy } from '@angular/core';
import { Question } from '@shared/model/itembank.models';
import { QuestionStatis, StudentScore } from '@shared/model/analysis.models';
import { TargetUser } from '@shared/target/create-target/create-target.component';
import { BehaviorSubject, Subject } from 'rxjs';
import {
  TaskAnalysis,
  TaskAnalysisScope,
  TaskClassDirector,
  TaskClassroomList,
  TaskPackDetail,
} from '@shared/model/task.models';
import { TaskService } from '@shared/service/task.service';
import { NzDrawerService } from 'ng-zorro-antd/drawer';
import { TaskPackChangeComponent } from '@app/task/task-analysis/components/task-pack-change/task-pack-change.component';
import { ClassLearnType } from '@app/analysis/model/enums/class-learn.enum';
import { TargetClassForm } from '@shared/target/target.models';
import { TargetService } from '@shared/target/target.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import dayjs from 'dayjs';
import { IntervalLooperHelper, RxjsSubscription } from '@common';
import { ActivatedRoute } from '@angular/router';
import { isNil, pickBy } from 'es-toolkit';

@Injectable()
export class TaskAnalysisService implements OnDestroy {
  public loadTaskTimer?: IntervalLooperHelper;
  public loadTaskModifyDate = null;
  public taskId: number = null;
  public taskPackId: number = null;
  public task: BehaviorSubject<TaskAnalysis> = new BehaviorSubject<TaskAnalysis>(null);
  public taskSubscription: RxjsSubscription = new RxjsSubscription();

  /**
   * 当前(获取数据/切换)用的taskId 有可能为taskId | 'my' | 'all' my为我的任教 all为年级全部
   * @since 24/10/22 增加 行政班 director 如果选中的行政版，则currentTaskId 置空
   */
  public currentTaskId: number | string = null;
  public taskPackDetails: TaskPackDetail[] = [];

  public allTaskPackDetails: TaskPackDetail[] = [];

  // 行政版数据
  public classDirector: TaskClassDirector[] = [];
  // 当前选中的行政版
  public currentClassroomId: number = null;

  // 举一反三题目列表
  public quickUptakeQuestions: BehaviorSubject<QuickUptakeQuestion[]> = new BehaviorSubject<QuickUptakeQuestion[]>([]);

  /**
   * 任务包详情（班级/任务包列表）写入信号
   * OnPush 下直读 classDirector/taskPackDetails 的组件需要订阅此信号刷新——
   * 这些字段在任务数据（task 流）之前异步写入，无此信号时会被推迟到 task 流触发才渲染
   */
  public taskPackDetailChange$: Subject<void> = new Subject<void>();

  unfinishedStudents$: Promise<string[]> = null;

  // 从“班主任列表”进入任务详情。
  isFromTaskList: boolean = false;

  get taskAnalysisQuestionEmpty() {
    return this.task.getValue()?.questionStatis?.length === 0;
  }

  get isClassroomTask() {
    return typeof this.currentTaskId === 'number';
  }

  /**
   * 返回 scope  taskId | 'my' | 'all' | 'director'
   */
  get scope() {
    return this.currentClassroomId && !this.currentTaskId
      ? 'director'
      : ((typeof this.currentTaskId === 'number' ? 'special' : this.currentTaskId) as 'special' | 'my' | 'all');
  }

  // 是否为AI批阅
  get aiMarkEnabled() {
    return !!this.task.getValue()?.aimarkOpened;
  }

  private taskSrv = inject(TaskService);
  private drawerSrv = inject(NzDrawerService);
  private targetClassSrv = inject(TargetService);
  private message = inject(NzMessageService);
  private route = inject(ActivatedRoute);

  ngOnDestroy() {
    this.loadTaskTimer?.destroy();
    this.taskSubscription.unsubscribe();
  }

  async initData() {
    const { id } = this.route.snapshot.params;
    await this.reloadTask(id);
    // 组件复用时 initData 只在首次执行；监听 :id 变化，跨任务跳转(task/5/analysis → task/7/analysis)时重载任务
    this.taskSubscription.add(
      this.route.params.subscribe(params => {
        const nextId = params['id'];
        if (nextId != null && String(this.taskId) !== String(nextId)) {
          this.reloadTask(nextId);
        }
      })
    );
  }

  /** 按 :id 初始化并加载任务（initData 首次调用与后续 :id 变化均走这里） */
  private async reloadTask(id: string) {
    this.taskId = Number(id);
    this.currentTaskId = Number(id);
    this.currentClassroomId = null;
    this.loadTaskModifyDate = null;
    this.loadTaskTimer?.destroy();
    // destroy() 会把内部 roundCall 置 null，此时父组件 router.events 的 NavigationEnd 仍会对旧 helper 调 start() 抛 TypeError，先置空以免短路
    this.loadTaskTimer = undefined;
    /**
     * @since 24/10/25
     * 此接口不依托任务包 id
     */
    await this.loadTaskPackDetail(this.taskId);
    this.loadTaskTimer = new IntervalLooperHelper(() => this.loadTask(), (Math.random() * 10 + 5) * 1000).start();
  }

  // 更新举一反三题目列表
  updateQuickUptakeQuestions(
    options: { label: string; value: string; students: StudentScore[] }[],
    question: Question,
    statis: QuestionStatis,
    sourceQuestion: Question
  ) {
    const quickUptakeQuestions = this.quickUptakeQuestions.getValue();
    const quickUptakeQuestion = quickUptakeQuestions.find(
      i => i.question.id === question.id && i.sourceQuestion.id === sourceQuestion.id
    );
    if (quickUptakeQuestion) {
      quickUptakeQuestion.selectOptions = options;
      quickUptakeQuestion.students = options.reduce((acc, i) => acc.concat(i.students), []);
    } else {
      // question.no = quickUptakeQuestions.length;
      quickUptakeQuestions.push({
        question,
        selectOptions: options,
        statis: statis,
        sourceQuestion: sourceQuestion,
        students: options.map(i => i.students).flat(),
      });
    }

    this.quickUptakeQuestions.next(quickUptakeQuestions);
  }

  // 删除举一反三题目中的题目
  deleteQuickUptakeQuestion(question: Question, sourceQuestion: Question) {
    const quickUptakeQuestions = this.quickUptakeQuestions.getValue();
    const quickUptakeQuestion = quickUptakeQuestions.find(
      i => i.question.id === question.id && i.sourceQuestion.id === sourceQuestion.id
    );
    if (quickUptakeQuestion) {
      quickUptakeQuestions.splice(quickUptakeQuestions.indexOf(quickUptakeQuestion), 1);
    }
    this.quickUptakeQuestions.next(quickUptakeQuestions);
  }

  // 获取数据
  async loadTask(first: boolean = true) {
    const params = this.getAggregateQueryParams();
    const { status, lastModified, body } = await this.taskSrv.getAnalysisV2(
      params.scope,
      params.params,
      this.loadTaskModifyDate ? { 'If-Modified-Since': this.loadTaskModifyDate } : {}
    );
    if (status !== 200) return;
    this.loadTaskModifyDate = lastModified;
    if (!body.taskPackId && first) this.taskPackDetails = [];
    if (body.taskPackId) {
      this.taskPackId = body.taskPackId;
    }

    // 如果已过结束日期一周,那么不再更新数据
    if (dayjs(body.endAt).isBefore(dayjs().subtract(1, 'week'))) {
      this.loadTaskTimer?.destroy();
    }

    this.task.next(body);

    this.unfinishedStudents$ = this.getTaskUnfinishedStudents();
  }

  // 获取任务包详情
  async loadTaskPackDetail(taskId: number) {
    /**
     * @since 24/10/22
     * 改为从normalData中取taskPackDetails
     */
    const result: TaskClassroomList = await this.taskSrv.getTaskClassroom(taskId);
    this.classDirector = result.classDirectorData;

    const { classId } = this.route.snapshot.queryParams;
    if (classId && this.classDirector.findIndex(x => x.classroomId === Number(classId)) > -1) {
      this.currentTaskId = null;
      this.loadTaskModifyDate = null;
      this.currentClassroomId = Number(classId);
    }
    this.isFromTaskList = !isNil(classId);
    // const details = await this.taskSrv.getTaskPackDetail(task.taskPackId);
    const details = result.normalData.map(i => {
      let taskId = null;
      if (i?.taskId) {
        taskId = i.taskId;
      } else {
        if (i.classroomName === '全部任教') {
          taskId = 'my';
        } else {
          taskId = 'all';
        }
      }
      return {
        ...i,
        taskId,
      };
    });
    if (this.classDirector?.length > 0) {
      this.taskPackDetails = details.filter(x => x.taskId !== this.currentTaskId);
    } else {
      this.taskPackDetails = details;
    }
    this.allTaskPackDetails = details;
    this.taskPackDetailChange$.next();
  }

  // 切换查看对象
  changeTask() {
    const drawer = this.drawerSrv.create({
      nzContent: TaskPackChangeComponent,
      nzTitle: '切换查看对象',
      nzWidth: 512,
      nzContentParams: {
        taskAnalysisSrv: this,
      },
      nzBodyStyle: {
        'background-color': '#F2F2F2',
      },
    });
    drawer.afterClose.subscribe(async (taskPackDetail: TaskPackDetail) => {
      if (taskPackDetail) {
        this.currentTaskId = taskPackDetail.taskId;
        this.currentClassroomId = null;
        this.loadTaskModifyDate = null;
        await this.loadTask(false);
      }
    });
  }

  getTaskUnfinishedStudents(): Promise<string[]> {
    const params = this.getAggregateQueryParams();
    return this.taskSrv.getTaskUnfinishedStudents(params.scope, params.params);
  }

  // 创建靶向班
  createTargetClassroom(gradeId: number, name: string, users: any[]) {
    this.targetClassSrv.createTargetClass({
      gradeId: gradeId,
      name: name,
      users: users as TargetUser[],
      type: ClassLearnType.Task,
      taskId: this.taskId,
      onSubmit: async (params: TargetClassForm) => {
        await this.targetClassSrv.submitTargetClass(ClassLearnType.Task, params);
      },
    });
  }

  // 获取多班级任务查询参数
  getAggregateQueryParams(): {
    scope: TaskAnalysisScope;
    params: {
      taskId?: number;
      taskPackId?: number;
      classroomId?: number;
    };
  } {
    let taskId = null;
    switch (this.scope) {
      case 'director': {
        const { id } = this.route.snapshot.params;
        taskId = Number(id);
        break;
      }
      case 'special':
        taskId = this.currentTaskId as number;
        break;
    }
    const params = {
      taskId: taskId,
      taskPackId: this.scope === 'special' || this.scope === 'director' ? null : this.taskPackId,
      classroomId: this.scope === 'director' ? this.currentClassroomId : null,
    };
    return {
      scope: this.scope,
      params,
    };
  }

  // 催交任务
  async remindTask(intelligentTaskId?: number) {
    const params = this.getAggregateQueryParams();
    /**
     * 催交增加对 错题重练任务的支持
     * 传递intelligentTaskId  代表错题重练任务
     */
    await this.taskSrv.remindTask(
      params.scope,
      pickBy({ ...params.params, intelligentTaskId: intelligentTaskId }, i => Boolean(i))
    );
    this.message.create('success', '催交成功');
  }

  /**
   * 立即发布错题重练任务
   * @param taskId 此时没有错题重练，所以 taskId 为 父任务 id
   */
  async publishIntelligentTask(taskId: number) {
    if (!taskId) return;
    try {
      await this.taskSrv.publishIntelligent(taskId);
      this.message.success('发布成功');
      this.loadTask();
    } catch (e) {
      this.message.error(e.error.detail ?? '发布失败');
    }
  }

  // 重算选择题
  async remarkChoice(taskId: number) {
    await this.taskSrv.remarkChoice(taskId);
    this.message.create('success', '已安排重算，请静候结果');
  }
}

export interface QuickUptakeQuestion {
  sourceQuestion: Question;
  question: Question;
  statis: QuestionStatis;
  selectOptions: Array<{ label: string; value: string; students: StudentScore[] }>;
  students: StudentScore[];
}

export enum TaskAnalysisTabsetEnum {
  MAIN = 'analysis', // 完成情况
  PROGRESS = 'progress', // 成绩详情
  CONTENT = 'content', // 任务详情
  COMPARE = 'compare', // 对比分析
  INTELLIGENT = 'intelligent', // 错题重练
  QUICK_UPTAKE = 'quick-uptake', // 举一反三
}
